import { FunctionComponent, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FFFStorage } from '@fff-web/fff-utilities';
import { getEnv } from '../../../../environment';
import { useUniversalNavigationContext } from '../../../context/UniversalNavigationContext';
import { REACT_APP_MOBILE_BANNER_URL } from '../../../utils/dynamicLinks';
import type { NewMobileBannerProps } from './types';
import fffAppLogoRounded from './fff-app-logo-rounded.png';
import mobileScreenshots from './mobile-screenshots.png';
import * as styles from './NewMobileBanner.module.css';

const BANNER_SHOWN_KEY = 'new_mobile_banner_shown_on_session';
const BANNER_TEXT_LOYALTY =
  'Earn Points, collect anniversary rewards, and redeem for Shopping Credit. VIP Member Rewards live exclusively in the FabFitFun app.';
const BANNER_TEXT_GENERAL =
  'Enjoy advanced filtering, sneak peeks into sales, seamless shopping and more from FabFitFun.';
const hostName = getEnv().REACT_APP_HOSTNAME;

const NewMobileBanner: FunctionComponent<NewMobileBannerProps> = ({
  onBannerClick,
  onBannerClose,
  appMobileLink = '',
  showLoyaltyLayout,
}) => {
  const { setAccessibilityButtonVisible } = useUniversalNavigationContext();
  const hasBeenShown = FFFStorage.getFromStorage({ key: BANNER_SHOWN_KEY });
  const isUniversalLink = appMobileLink.includes(REACT_APP_MOBILE_BANNER_URL);
  const ButtonComponent = isUniversalLink ? 'a' : 'button';
  const buttonCustomProps = isUniversalLink ? { href: appMobileLink } : { type: 'button' as const };
  const bannerText = showLoyaltyLayout ? BANNER_TEXT_LOYALTY : BANNER_TEXT_GENERAL;
  const imageRef = useRef<HTMLImageElement>(null);
  const loyaltyImage = `https://scontent01.${hostName}/homepage/cdn/mobile-loyalty.png`;
  const bannerImage = showLoyaltyLayout ? loyaltyImage : mobileScreenshots;

  const handleBannerClose = () => {
    document.body.classList.remove('scrollContain');
    setAccessibilityButtonVisible(true);
    onBannerClose(false, BANNER_SHOWN_KEY);
  };
  const handleImageFallback = () => {
    if (imageRef.current) {
      imageRef.current.onerror = null;
      imageRef.current.src = mobileScreenshots;
    }
  };

  const MobileBannerButtons = useMemo(
    () => (
      <div className={styles.buttonContainer}>
        <ButtonComponent
          {...buttonCustomProps}
          onClick={onBannerClick}
          className={styles.openButton}
          data-testid="open-link-mobile-banner-button"
        >
          Open FabFitFun
        </ButtonComponent>
        <button
          onClick={handleBannerClose}
          type="button"
          className={styles.closeNewMobileBannerText}
          data-testid="close-new-mobile-banner-button"
        >
          Continue to mobile site
        </button>
      </div>
    ),
    [ButtonComponent, buttonCustomProps, handleBannerClose, onBannerClick],
  );

  useEffect(() => {
    if (hasBeenShown) return;
    setAccessibilityButtonVisible(false);
  }, [setAccessibilityButtonVisible, hasBeenShown]);

  useEffect(() => {
    if (hasBeenShown) return;
    document.body.classList.add('scrollContain');
    return () => {
      document.body.classList.remove('scrollContain');
    };
  }, [hasBeenShown]);

  if (hasBeenShown) return null;
  return createPortal(
    <div className={styles.container} id="mobile-web-banner">
      <div className={styles.bannerContainer}>
        <div className={styles.headerContainer}>
          <img src={fffAppLogoRounded} alt="app-logo-rounded" className={styles.logoImage} />
          <div className={styles.textContainer}>
            <div className={styles.title}>Experience more in the app</div>
            <div className={styles.description}>{bannerText}</div>
          </div>
        </div>
        {!showLoyaltyLayout && MobileBannerButtons}
        <img
          ref={imageRef}
          src={bannerImage}
          alt="mobile-screenshots"
          onError={handleImageFallback}
          className={styles.mobileImageContainer}
        />
        {showLoyaltyLayout && MobileBannerButtons}
      </div>
    </div>,
    document.body,
  );
};

export default NewMobileBanner;
